import React from 'react';
import { cn } from '../../lib/utils';

export interface CardProps {
  /** Card header content */
  header?: React.ReactNode;
  /** Card body content */
  children: React.ReactNode;
  /** Card footer content */
  footer?: React.ReactNode;
  /** Custom className for the card container */
  className?: string;
  /** Enable hover effect */
  hover?: boolean;
}

/**
 * Card component with optional header and footer
 *
 * @example
 * ```tsx
 * <Card header={<h2>Title</h2>}>
 *   <p>Card content goes here</p>
 * </Card>
 *
 * <Card
 *   header={<h2>Campaign Details</h2>}
 *   footer={<Button>Edit</Button>}
 * >
 *   <div>Campaign information...</div>
 * </Card>
 * ```
 */
export const Card: React.FC<CardProps> = ({
  header,
  children,
  footer,
  className,
  hover = false,
}) => {
  return (
    <div
      className={cn(
        'card',
        hover && 'card-hover',
        className
      )}
    >
      {header && (
        <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-200/60">
          {header}
        </div>
      )}
      <div className="p-4 md:p-6">
        {children}
      </div>
      {footer && (
        <div className="px-4 py-3 md:px-6 md:py-4 border-t border-gray-200/60 bg-gray-50/50">
          {footer}
        </div>
      )}
    </div>
  );
};

Card.displayName = 'Card';
